/* ==========================================================================
   Collection hero carousel
   Vanilla custom element, no dependencies.

   Two things drive the design:

   1. Slides are stacked, not laid out in a long strip. Each transition moves
      the outgoing slide one full width out and the incoming slide one full
      width in, which looks identical to a track but makes every move a single
      step — wrapping from the last collection to the first slides forwards
      instead of rewinding past everything in between.

   2. Autoplay is derived, never toggled. Anything that should pause it adds a
      reason to a set; the timer runs only while that set is empty. A missed
      mouseleave or a stale observer can therefore never wedge it permanently.

   Without JavaScript the first slide renders as a normal hero and every CTA is
   a real link, so the section degrades cleanly.
   ========================================================================== */

if (!customElements.get('neo-collection-hero')) {
  customElements.define(
    'neo-collection-hero',
    class NeoCollectionHero extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;

        this.slides = Array.from(this.querySelectorAll('[data-chero-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-chero-dot]'));
        this.liveRegion = this.querySelector('[data-chero-live]');
        this.playToggle = this.querySelector('[data-chero-play]');
        this.count = this.slides.length;
        if (!this.count) return;

        this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
        this.interval = Math.max(1, parseFloat(this.dataset.speed) || 5) * 1000;
        this.autoplayEnabled = this.dataset.autoplay === 'true' && this.count > 1;

        // Every reason autoplay is currently held. Empty set === running.
        this.holds = new Set();
        this.timer = null;
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (this.motionQuery.matches) this.holds.add('motion');
        if (document.hidden) this.holds.add('hidden');

        this.bind();
        this.paint();
        this.sync();

        if (this.playToggle && !this.autoplayEnabled) this.playToggle.hidden = true;
        this.ready = true;
      }

      disconnectedCallback() {
        this.stop();
        window.clearTimeout(this.settleTimer);
        this.clearSettleListener();
        if (this.observer) this.observer.disconnect();
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.onMotion && this.motionQuery) this.motionQuery.removeEventListener('change', this.onMotion);
        this.ready = false;
      }

      /* ------------------------------------------------------------------
         Playback state
         ------------------------------------------------------------------ */

      hold(reason) {
        this.holds.add(reason);
        this.sync();
      }

      release(reason) {
        this.holds.delete(reason);
        this.sync();
      }

      /** Starts or stops the timer so it matches the current hold set. */
      sync() {
        const shouldRun = this.autoplayEnabled && this.count > 1 && this.holds.size === 0;
        if (shouldRun && !this.timer) {
          this.timer = window.setInterval(() => this.step(1), this.interval);
        } else if (!shouldRun && this.timer) {
          this.stop();
        }
      }

      stop() {
        if (this.timer) window.clearInterval(this.timer);
        this.timer = null;
      }

      /** Restarts the countdown so a manual move gets a full dwell. */
      resetTimer() {
        if (!this.timer) return;
        this.stop();
        this.sync();
      }

      /* ------------------------------------------------------------------
         Navigation
         ------------------------------------------------------------------ */

      /** +1 or -1: whichever way round the loop is shorter. */
      directionTo(target) {
        let diff = target - this.index;
        if (diff > this.count / 2) diff -= this.count;
        if (diff < -this.count / 2) diff += this.count;
        return diff >= 0 ? 1 : -1;
      }

      step(delta, manual) {
        this.goTo((this.index + delta + this.count) % this.count, manual);
      }

      goTo(target, manual) {
        this.finishSettle();
        const index = ((target % this.count) + this.count) % this.count;

        if (manual) {
          // Once a visitor takes control, changes are worth announcing.
          if (this.liveRegion) this.liveRegion.setAttribute('aria-live', 'polite');
          this.resetTimer();
        }

        if (index === this.index) return;

        const previous = this.index;
        const forward = this.directionTo(index) > 0;
        const outgoing = this.slides[previous];
        const incoming = this.slides[index];

        // Park the incoming slide on its entry side with transitions off, and
        // commit that position. Without this the browser animates the jump
        // from wherever the slide was left last time, which is what made the
        // wrap-around — and every move after it — travel the wrong way.
        incoming.classList.add('is-preparing');
        incoming.style.setProperty('--chero-x', forward ? '100%' : '-100%');
        void incoming.offsetWidth;
        incoming.classList.remove('is-preparing');

        // The outgoing slide animates to its exit side as it loses .is-active.
        // Setting this while it is still active has no visual effect.
        outgoing.style.setProperty('--chero-x', forward ? '-100%' : '100%');

        this.index = index;
        this.paint();
      }

      /** Reflects the current index in the DOM. */
      paint() {
        // `visibility: hidden` on inactive slides keeps them out of the tab
        // order and the accessibility tree, so no aria bookkeeping is needed.
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));

        this.dots.forEach((dot, i) => {
          if (i === this.index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });

        if (this.liveRegion) this.liveRegion.textContent = `${this.index + 1} / ${this.count}`;
      }

      /* ------------------------------------------------------------------
         Events
         ------------------------------------------------------------------ */

      bind() {
        const prev = this.querySelector('[data-chero-prev]');
        const next = this.querySelector('[data-chero-next]');
        if (prev) prev.addEventListener('click', () => this.step(-1, true));
        if (next) next.addEventListener('click', () => this.step(1, true));

        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i, true)));

        if (this.playToggle) {
          this.playToggle.addEventListener('click', () => {
            const paused = !this.holds.has('user');
            this.playToggle.setAttribute('aria-pressed', String(paused));

            const strings = window.accessibilityStrings || {};
            const label = paused ? strings.playSlideshow : strings.pauseSlideshow;
            if (label) this.playToggle.setAttribute('aria-label', label);

            if (paused) this.hold('user');
            else this.release('user');
          });
        }

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1, true);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(1, true);
          }
        });

        // Hover only pauses over the copy and controls. Covering the whole
        // hero would freeze it for good, because at this size the pointer is
        // almost always somewhere inside it.
        this.querySelectorAll('.neo-chero__col').forEach((zone) => {
          zone.addEventListener('pointerenter', () => this.hold('hover'));
          zone.addEventListener('pointerleave', () => this.release('hover'));
        });
        // Safety net for a pointerleave the browser never delivers, e.g. when
        // the pointer exits the window or the layout shifts under it.
        this.addEventListener('pointerleave', () => this.release('hover'));

        // Pause for keyboard users only. Clicking a dot also focuses it, and
        // holding on plain focus left autoplay stopped for good afterwards.
        // The modality is tracked explicitly rather than read from
        // :focus-visible, whose heuristics vary between browsers.
        this.addEventListener(
          'pointerdown',
          () => {
            this.pointerFocus = true;
            // Cleared after the focus event that this press is about to fire.
            window.setTimeout(() => {
              this.pointerFocus = false;
            }, 0);
          },
          true
        );

        this.addEventListener('focusin', () => {
          if (this.pointerFocus) return;
          this.hold('focus');
        });

        this.addEventListener('focusout', (event) => {
          if (this.contains(event.relatedTarget)) return;
          this.release('focus');
        });

        this.bindSwipe();

        this.onVisibility = () => {
          if (document.hidden) {
            this.hold('hidden');
            return;
          }
          // Coming back to the tab: drop every hold that depends on an event
          // the browser may not replay, and re-measure rather than trusting
          // an observer that might not fire again.
          this.holds.delete('hidden');
          this.holds.delete('hover');
          this.holds.delete('drag');
          if (this.isInViewport()) this.holds.delete('offscreen');
          this.sync();
        };
        document.addEventListener('visibilitychange', this.onVisibility);

        this.onMotion = (event) => {
          if (event.matches) this.hold('motion');
          else this.release('motion');
        };
        this.motionQuery.addEventListener('change', this.onMotion);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries[entries.length - 1].isIntersecting) this.release('offscreen');
              else this.hold('offscreen');
            },
            { threshold: 0 }
          );
          this.observer.observe(this);
        }
      }

      /**
       * Drag to change slides. The slide tracks the pointer one-to-one while
       * held, with the neighbour it is heading towards revealed alongside it,
       * then settles either onto that neighbour or back home on release.
       *
       * Pointer events cover mouse, touch and pen through one path, and
       * `touch-action: pan-y` on the viewport keeps vertical page scrolling
       * native while we take the horizontal axis.
       */
      bindSwipe() {
        if (this.count < 2) return;

        const SLOP = 8; // px before the axis of the gesture is decided
        const drag = { id: null, axis: null, startX: 0, startY: 0, width: 0, peek: null, dir: 0 };
        this.drag = drag;
        let swiped = false;

        const clearPeek = () => {
          if (!drag.peek) return;
          drag.peek.classList.remove('is-peek');
          drag.peek.style.transform = '';
          drag.peek = null;
        };

        const endGesture = () => {
          drag.id = null;
          drag.axis = null;
          drag.dir = 0;
          this.classList.remove('is-dragging');
          this.release('drag');
        };

        this.addEventListener('pointerdown', (event) => {
          if (drag.id !== null) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;

          // A press during the settle animation takes over from it.
          this.finishSettle();

          drag.id = event.pointerId;
          drag.startX = event.clientX;
          drag.startY = event.clientY;
          drag.axis = null;
          drag.width = this.getBoundingClientRect().width || 1;
          swiped = false;
          this.hold('drag');
        });

        this.addEventListener('pointermove', (event) => {
          if (event.pointerId !== drag.id || drag.axis === 'y') return;

          const dx = event.clientX - drag.startX;
          const dy = event.clientY - drag.startY;

          if (drag.axis === null) {
            if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;

            if (Math.abs(dy) > Math.abs(dx)) {
              // The visitor is scrolling the page, not swiping the carousel.
              drag.axis = 'y';
              endGesture();
              return;
            }

            drag.axis = 'x';
            this.classList.add('is-dragging');
            try {
              this.setPointerCapture(event.pointerId);
            } catch (error) {
              /* capture is best effort */
            }
          }

          // Which neighbour is being pulled into view.
          const dir = dx < 0 ? 1 : -1;
          if (dir !== drag.dir) {
            clearPeek();
            drag.dir = dir;
            drag.peek = this.slides[(this.index + dir + this.count) % this.count];
            drag.peek.classList.add('is-peek');
          }

          if (!drag.peek) return;

          const width = drag.width;
          this.slides[this.index].style.transform = `translateX(${dx}px)`;
          drag.peek.style.transform = `translateX(${dir === 1 ? width + dx : dx - width}px)`;
        });

        const finish = (event) => {
          if (event.pointerId !== drag.id) return;

          if (drag.axis !== 'x') {
            endGesture();
            return;
          }

          const dx = event.clientX - drag.startX;
          const dir = drag.dir;
          const width = drag.width;
          const current = this.slides[this.index];
          const peek = drag.peek;

          // Past roughly a sixth of the hero, let it through.
          const commit = Math.abs(dx) > Math.min(90, width * 0.16);

          // Transitions come back on, so the slides ease to where they land.
          this.classList.remove('is-dragging');

          if (commit && peek) {
            swiped = true;
            current.style.transform = `translateX(${dir === 1 ? -width : width}px)`;
            peek.style.transform = 'translateX(0px)';
            this.settleTo((this.index + dir + this.count) % this.count, dir);
          } else {
            current.style.transform = 'translateX(0px)';
            if (peek) peek.style.transform = `translateX(${dir === 1 ? width : -width}px)`;
            this.settleTo(this.index, dir);
          }

          drag.id = null;
          drag.axis = null;
          drag.dir = 0;
          this.release('drag');
        };

        this.addEventListener('pointerup', finish);
        this.addEventListener('pointercancel', (event) => {
          if (event.pointerId !== drag.id) return;
          endGesture();
          clearPeek();
          this.slides[this.index].style.transform = '';
        });

        // A drag that ends on the CTA must not also follow the link.
        this.addEventListener(
          'click',
          (event) => {
            if (!swiped) return;
            swiped = false;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );

        // Stop the browser's native image/text drag hijacking the gesture.
        this.addEventListener('dragstart', (event) => event.preventDefault());
      }

      /**
       * Hands a released drag back to the class-driven state once it has eased
       * into place. Inline transforms are only cleared after --chero-x matches
       * them, so nothing jumps at the handover.
       */
      settleTo(index, dir) {
        this.pendingSettle = { index, dir };

        const target = this.slides[index];
        const done = (event) => {
          // transitionend bubbles: ignore descendants such as the CTA, whose
          // own transform transition would otherwise end the settle early.
          if (event && (event.target !== target || event.propertyName !== 'transform')) return;
          this.finishSettle();
        };

        this.clearSettleListener();
        this.settleListener = { target, done };
        target.addEventListener('transitionend', done);

        // Fallback for the case where nothing actually moves, so no
        // transitionend is ever fired.
        window.clearTimeout(this.settleTimer);
        this.settleTimer = window.setTimeout(() => this.finishSettle(), this.duration() + 120);
      }

      clearSettleListener() {
        if (!this.settleListener) return;
        this.settleListener.target.removeEventListener('transitionend', this.settleListener.done);
        this.settleListener = null;
      }

      finishSettle() {
        if (!this.pendingSettle) return;

        const { index, dir } = this.pendingSettle;
        const previous = this.index;
        const peek = this.drag && this.drag.peek;

        window.clearTimeout(this.settleTimer);
        this.clearSettleListener();
        this.pendingSettle = null;

        if (index !== previous) {
          // The slide left behind rests on the side it exited towards.
          this.slides[previous].style.setProperty('--chero-x', dir === 1 ? '-100%' : '100%');
        }
        if (peek && peek !== this.slides[index]) {
          peek.style.setProperty('--chero-x', dir === 1 ? '100%' : '-100%');
        }

        this.index = index;
        this.paint();

        if (peek) {
          peek.classList.remove('is-peek');
          if (peek !== this.slides[index]) peek.style.transform = '';
          this.drag.peek = null;
          this.drag.dir = 0;
        }

        this.slides.forEach((slide) => {
          slide.style.transform = '';
        });

        this.resetTimer();
      }

      /** Transition length in ms, read from the stylesheet. */
      duration() {
        const raw = window.getComputedStyle(this).getPropertyValue('--chero-fade').trim();
        const value = parseFloat(raw) || 600;
        return raw.endsWith('ms') || !raw.endsWith('s') ? value : value * 1000;
      }

      isInViewport() {
        const rect = this.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
      }
    }
  );
}
